/**
 * 遥测数据 Store（Zustand）
 * 存储实时遥测帧、电机状态，管理遥测数据缓冲区
 */
import { create } from 'zustand';
import type { TelemetryFrame, MotorStatus } from '@fluxlab/protocols';

/** 遥测缓冲区最大帧数 */
const MAX_BUFFER_SIZE = 3000;

interface TelemetryStore {
  /** Latest telemetry frame */
  latest: TelemetryFrame | null;
  /** Latest motor status */
  status: MotorStatus | null;
  /** Ring buffer of recent telemetry frames for charts */
  buffer: TelemetryFrame[];
  /** Whether high-speed streaming is active */
  streaming: boolean;
  /** Current sample rate */
  sampleRate: number;

  setLatest: (frame: TelemetryFrame) => void;
  setStatus: (status: MotorStatus) => void;
  addFrame: (frame: TelemetryFrame) => void;
  addFrames: (frames: TelemetryFrame[]) => void;
  setStreaming: (streaming: boolean, sampleRate?: number) => void;
  clearBuffer: () => void;
}

/** 遥测数据 Store（Zustand）：存储实时遥测帧、电机状态并管理数据缓冲区 */
export const useTelemetryStore = create<TelemetryStore>((set, get) => ({
  latest: null,
  status: null,
  buffer: [],
  streaming: false,
  sampleRate: 0,

  setLatest: (frame) => set({ latest: frame }),
  setStatus: (status) => set({ status }),
  addFrame: (frame) => get().addFrames([frame]),
  addFrames: (frames) => {
    if (frames.length === 0) return;
    const { buffer } = get();
    const incoming = frames.length >= MAX_BUFFER_SIZE
      ? frames.slice(-MAX_BUFFER_SIZE)
      : frames;
    const keepExisting = Math.max(0, MAX_BUFFER_SIZE - incoming.length);
    const existing = keepExisting > 0 ? buffer.slice(-keepExisting) : [];
    const newBuffer = [...existing, ...incoming];
    set({ latest: incoming[incoming.length - 1], buffer: newBuffer });
  },
  setStreaming: (streaming, sampleRate = 0) => set({ streaming, sampleRate }),
  clearBuffer: () => set({ buffer: [], latest: null }),
}));
